import React, { useState, useEffect } from 'react';
import { fetchTagUsageReport, fetchDashboardStats, fetchBadgeUsers } from '../services/adminApi';

export default function AdminReportsPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Pagination state
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState('clip_count');

  // Search/filter state
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Load appropriate data when pagination/sort changes or tab switches
    if (activeTab === 'tags') {
      loadTagReport();
    }
  }, [limit, offset, sort, activeTab]);

  useEffect(() => {
    if (activeTab === 'badges') {
      loadBadgesReport();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'overview') {
      loadOverviewStats();
    }
  }, [activeTab]);

  async function loadTagReport() {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchTagUsageReport(limit, offset, sort);
      setTags(res.tags || []);
      setTotal(res.total || 0);
    } catch (e) {
      setError(e.message);
      setTags([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadOverviewStats() {
    try {
      setStatsLoading(true);
      const res = await fetchDashboardStats();
      setStats(res || {});
    } catch (e) {
      // keep stats null on error
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }

  // Load badges and awarded users
  const [badges, setBadges] = useState([]);
  const [badgesLoading, setBadgesLoading] = useState(false);
  const [badgesError, setBadgesError] = useState(null);

  async function loadBadgesReport() {
    try {
      setBadgesLoading(true);
      setBadgesError(null);
      const res = await fetchBadgeUsers();
      // res.badges is expected to be an array of { id, key, name, icon_url, users: [] }
      setBadges(res.badges || []);
    } catch (e) {
      // Provide a clearer hint for admins and try the debug endpoint to get more detail
      const hint = 'Ensure you are signed in as an admin and the backend is running.';
      /* c8 ignore start */
      let msg = (e && e.message) ? `${e.message}. ${hint}` : `Unable to load badges. ${hint}`;
      /* c8 ignore stop */
      try {
        const debugRes = await fetch('/api/admin/reports/badge-users-debug', { credentials: 'include' });
        if (debugRes.ok) {
          const dbg = await debugRes.json();
          /* c8 ignore start */
          if (dbg && dbg.ok === false && dbg.error) {
          /* c8 ignore stop */
            msg += ` Debug: ${dbg.error}`;
            /* c8 ignore start */
            if (dbg.stack) msg += `\nStack: ${dbg.stack}`;
            /* c8 ignore stop */
          }
        } else {
          const txt = await debugRes.text().catch(() => null);
          /* c8 ignore start */
          if (txt) msg += ` Debug response: ${txt}`;
          /* c8 ignore stop */
        }
      } catch (dbgErr) {
        // ignore debug endpoint errors (likely not available in prod)
      }
      setBadgesError(msg);
      setBadges([]);
    } finally {
      setBadgesLoading(false);
    }
  }

  const resolveIconUrl = (iconUrl) => {
    /* c8 ignore start */
    if (!iconUrl) return null;
    /* c8 ignore stop */
    /* c8 ignore start */
    if (/^https?:\/\//i.test(iconUrl)) return iconUrl;
    return iconUrl.startsWith('/') ? iconUrl : `/${iconUrl}`;
    /* c8 ignore stop */
  };

  function handleSortChange(field) {
    /* c8 ignore start */
    if (sort === field) {
      setSort(field === 'clip_count' ? 'user_count' : 'clip_count');
    } else {
      setSort(field);
    }
    /* c8 ignore stop */
    setOffset(0);
  }

  // Filter tags based on search term
  const filteredTags = tags.filter(tag =>
    tag.tag.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pages = Math.ceil(total / limit) || 1;
  const currentPage = Math.floor(offset / limit) + 1;

  const hasPreviousPage = offset > 0;
  const hasNextPage = currentPage < pages;

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Reports</h1>
        <p className="text-gray-400 mt-1">Platform analytics and insights</p>
      </div>

      <div className="bg-dark-800 border border-dark-700 rounded-lg p-6">
        {/* Tab Navigation */}
        <div className="mb-6 border-b border-dark-600">
        <div className="flex space-x-4">
          <button
            onClick={() => { setActiveTab('overview'); loadOverviewStats(); }}
            className={`px-4 py-2 border-b-2 transition ${
              activeTab === 'overview'
                ? 'border-primary text-primary font-semibold'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('badges')}
            className={`px-4 py-2 border-b-2 transition ${
              activeTab === 'badges'
                ? 'border-primary text-primary font-semibold'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            Badges
          </button>
          <button
            onClick={() => setActiveTab('tags')}
            className={`px-4 py-2 border-b-2 transition ${
              activeTab === 'tags'
                ? 'border-primary text-primary font-semibold'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            Tag Usage
          </button>
        </div>
      </div>

      {activeTab === 'overview' && (
        <div>
          <h2 className="text-2xl font-bold mb-6">Overview</h2>
          {statsLoading ? (
            <p>Loading…</p>
          ) : stats ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-dark-800 border border-dark-600 rounded-lg">
                <div className="text-sm text-gray-400">Total Clips</div>
                <div className="text-2xl font-bold text-white">{stats.total_clips ?? '—'}</div>
              </div>
              <div className="p-4 bg-dark-800 border border-dark-600 rounded-lg">
                <div className="text-sm text-gray-400">Active Creators</div>
                <div className="text-2xl font-bold text-white">{stats.active_creators ?? '—'}</div>
              </div>
              <div className="p-4 bg-dark-800 border border-dark-600 rounded-lg">
                <div className="text-sm text-gray-400">Active Clippers</div>
                <div className="text-2xl font-bold text-white">{stats.active_clippers ?? '—'}</div>
              </div>
              <div className="p-4 bg-dark-800 border border-dark-600 rounded-lg">
                <div className="text-sm text-gray-400">Total Tags</div>
                <div className="text-2xl font-bold text-white">{stats.total_tags ?? '—'}</div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-dark-800 border border-dark-600 rounded-lg text-sm text-gray-400">Unable to load stats.</div>
          )}
        </div>
      )}

      {activeTab === 'badges' && (
        <div>
          <h2 className="text-2xl font-bold mb-6">Badges</h2>
          {badgesLoading ? (
            <p>Loading…</p>
          ) : badgesError ? (
            <div className="mb-4 p-4 bg-red-900 text-red-100 rounded-lg">Error: {badgesError}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {badges.map(b => (
                <div key={b.id} className="bg-dark-800 border border-dark-600 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {b.icon_url ? (
                        <img src={resolveIconUrl(b.icon_url)} alt={b.name} className="w-10 h-10 rounded" />
                      ) : (
                        <span className="text-2xl">🏅</span>
                      )}
                      <div className="text-sm text-gray-300">{b.name}</div>
                    </div>
                    <div className="text-2xl font-bold text-white">{(b.users && b.users.length) || 0}</div>
                  </div>
                  <div className="text-xs text-gray-400 mb-2">Users awarded this badge (Clipto users only)</div>
                  <div className="max-h-40 overflow-auto">
                    {b.users && b.users.length > 0 ? (
                      <ul className="space-y-2">
                        {b.users.map(u => (
                          <li key={u.user_id} className="flex items-center justify-between">
                            <div>
                              <div className="text-sm text-white">{u.display_name || u.username}</div>
                              <div className="text-xs text-gray-500">{u.username}</div>
                            </div>
                            <div className="text-xs text-gray-400">{new Date(u.earned_at).toLocaleString()}</div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500">No Clipto users have been awarded this badge.</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'tags' && (
        <>
          <h2 className="text-2xl font-bold mb-6">Tag Usage</h2>
          {error && (
            <div className="mb-4 p-4 bg-red-900 text-red-100 rounded-lg">
              Error: {error}
            </div>
          )}

          {/* Search/Filter Section */}
          <div className="mb-6 p-4 bg-dark-800 border border-dark-600 rounded-lg">
            <label className="block text-smalfont-medium text-gray-300 mb-2">
              Search Tags
            </label>
            <input
              type="text"
              placeholder="Filter by tag name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 bg-dark-900 border border-dark-600 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
            />
          </div>

      {/* Table Section */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-dark-600 bg-dark-800">
              <th className="text-left px-4 py-3 font-semibold text-gray-300">
                Tag
              </th>
              <th
                className="text-right px-4 py-3 font-semibold text-gray-300 cursor-pointer hover:text-blue-400"
                onClick={() => handleSortChange('clip_count')}
                title="Click to sort"
              >
                Clips
                {sort === 'clip_count' && (
                  <span className="ml-1 text-blue-400">↓</span>
                )}
              </th>
              {/* c8 ignore start */}
              <th
                className="text-right px-4 py-3 font-semibold text-gray-300 cursor-pointer hover:text-blue-400"
                onClick={() => handleSortChange('user_count')}
                title="Click to sort"
              >
                Users
                {sort === 'user_count' && (
                  <span className="ml-1 text-blue-400">↓</span>
                )}
              </th>
              {/* c8 ignore stop */}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="3" className="px-4 py-8 text-center text-gray-400">
                  Loading report...
                </td>
              </tr>
            ) : filteredTags.length === 0 ? (
              <tr>
                <td colSpan="3" className="px-4 py-8 text-center text-gray-400">
                  {searchTerm
                    ? `No tags matching "${searchTerm}"`
                    : 'No tags found'}
                </td>
              </tr>
            ) : (
              filteredTags.map((tagData) => (
                <tr
                  key={tagData.tag}
                  className="border-b border-dark-600 hover:bg-dark-800 transition-colors"
                >
                  <td className="px-4 py-3 text-gray-100">{tagData.tag}</td>
                  <td className="text-right px-4 py-3 text-gray-300">
                    {tagData.clip_count}
                  </td>
                  <td className="text-right px-4 py-3 text-gray-300">
                    {tagData.user_count}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {!loading && total > limit && (
        <div className="mt-6 flex items-center justify-between p-4 bg-dark-800 border border-dark-600 rounded-lg">
          <div className="text-sm text-gray-400">
            Page {currentPage} of {pages} (Total: {total} tags)
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={!hasPreviousPage}
              className={`px-4 py-2 rounded transition-colors ${
                hasPreviousPage
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-dark-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              Previous
            </button>

            {/* c8 ignore start */}
            <button
              onClick={() => setOffset(offset + limit)}
              disabled={!hasNextPage}
              className={`px-4 py-2 rounded transition-colors ${
                hasNextPage
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-dark-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              Next
            </button>
            {/* c8 ignore stop */}
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="mt-6 p-4 bg-dark-800 border border-dark-600 rounded-lg text-sm text-gray-400">
        <p>
          This report shows tag usage across all clips curated in Clipto. Click column headers to change sort order.
        </p>
      </div>
        </>
      )}
      </div>
    </div>
  );
}
